/**
 * @file Wise 端首页组件
 */

import {Component} from 'san';
import {gsap} from 'gsap';
import {ScrollTrigger} from 'gsap/ScrollTrigger';
import './index.less';
import Button from '@baidu/cosmic/button';
import Icon from '@baidu/cosmic/icon';
import Toast from '@baidu/cosmic/toast';
import {isWeixinApp} from '@searchfe/user-agent';
import DownloadBubble from '../download-bubble/index';
import VideoPlyr from '../video-plyr/index';
import Footer from '../footer/index';
import GuideMarker from '../guide-marker/index';
import {
    LOGO_URL_WHITE,
    POSTER_URL,
    VIDEO_URL,
    PAGE_TITLE_1,
    PAGE_TITLE_2,
    PAGE_SUBTITLE,
    SLOGAN,
    DOWNLOAD_ANDROID_URL,
    DOWNLOAD_IOS_URL,
    WISE_BG_DARK,
    WISE_BG_LIGHT,
    PRODUCT_BG_1,
    PRODUCT_BG_2,
    PRODUCT_BG_3,
    PRODUCT_BG_4,
} from '../../../../common/const/index';
import {isIOS, isAndroid} from '../../../../utils/ua';
import {handleLog} from '../../../../utils/index';

export default class App extends Component {
    static template = /* html */`
        <div>
            <div class="wise-container" s-ref="wiseContainer" style="background-image: url({{wiseBgLight}})">
                <div
                    id="wise-section-1"
                    class="wise-section-1 wise-section"
                    s-ref="wiseSection1"
                    style="background-image: url({{wiseBgDark}})"
                >
                    <div class="wise-section-logo">
                        <img class="site-logo" src="{{logoWhite}}" alt="心响"/>
                        <p class="site-slogan">{{slogan}}</p>
                    </div>
                    <div class="wise-section-main" s-show="isBackgroundLoaded">
                        <div class="text-wrap">
                            <p>{{title1}}</p>
                            <p>{{title2}}</p>
                        </div>
                        <div class="subtext-wrap">
                            <p>{{subTitle}}</p>
                        </div>
                        <video-plyr
                            class="video-wrap"
                            poster="{{posterUrl}}"
                            video-url="{{videoUrl}}"
                            on-play="handleVidoe('play')"
                            on-pause="handleVidoe('pause')"
                            on-ended="handleVidoe('ended')"
                        ></video-plyr>
                    </div>
                    <page-footer class="wise-container-footer"/>
                </div>
                <fragment s-for="item, index in productBgList">
                    <div
                        id="wise-section-{{index + 2}}"
                        class="wise-section-{{index + 2}} wise-section"
                        s-ref="wiseSection{{index + 2}}"
                    >
                        <div class="wise-section-img wise-section-img-{{index + 1 }}" style="background-image: url({{item}})"></div>
                    </div>
                </fragment>

                <div class="pagination">
                    <div
                        s-for="item, index in paginationList"
                        class="pagination-item {{ item ? 'active' : '' }}"
                        style="background-color: {{ paginationColor }}"
                    >
                    </div>
                </div>

                <div class="bottom-mask"></div>
                <div class="download-warp {{ !isFirstScreen ? 'mask-warp' : '' }}">
                    <cos-button
                        class="download-warp-btn"
                        on-click="handleDownloadClick"
                    >
                        <span class="download-warp-btn-icon"></span>
                        <span>下载心响APP</span>
                    </cos-button>
                </div>
                <div class="guide-warp" s-if="{{showGuide}}">
                    <guide-marker />
                </div>
            </div>
        </div>
    `;

    static components = {
        'download-bubble': DownloadBubble,
        'video-plyr': VideoPlyr,
        'cos-button': Button,
        'cos-icon': Icon,
        'page-footer': Footer,
        'guide-marker': GuideMarker,
    };

    initData() {
        return {
            logoWhite: LOGO_URL_WHITE,
            posterUrl: POSTER_URL,
            videoUrl: VIDEO_URL,
            wiseBgDark: WISE_BG_DARK,
            wiseBgLight: WISE_BG_LIGHT,
            paginationList: [1, 0, 0, 0, 0],
            paginationColor: '#fff',
            isFirstScreen: true,
            rootFontSize: 100,
            title1: PAGE_TITLE_1,
            title2: PAGE_TITLE_2,
            subTitle: PAGE_SUBTITLE,
            slogan: SLOGAN,
            downloadAndroidUrl: DOWNLOAD_ANDROID_URL,
            downloadIosUrl: DOWNLOAD_IOS_URL,
            downloadDom: null,
            originHeight: '',
            productBgList: [PRODUCT_BG_1, PRODUCT_BG_2, PRODUCT_BG_3, PRODUCT_BG_4],
            isBackgroundLoaded: false,
            showGuide: false,
        };
    }

    created(): void {
        gsap.registerPlugin(ScrollTrigger);
        ScrollTrigger.defaults({
            scroller: '.wise-container',
        });
    }

    attached(): void {
        window.addEventListener('resize', this.sectionHeight.bind(this));
        this.sectionHeight();

        this.backgroundAnimate();
        this.paginationAnimate();
        this.imageAnimate();
        this.buttonAnimate();

        this.watch('paginationList', (newVal: number[]) => {
            this.data.set('isFirstScreen', !!newVal[0]);
            const originHeight = this.data.get('originHeight');
            if (newVal[1] && originHeight) {
                const section1 = this.ref('wiseSection1');
                // 等动画结束后再手动恢复首屏高度
                setTimeout(() => {
                    if (section1 instanceof HTMLElement) {
                        section1.style.height = originHeight;
                    }
                }, 1000);
            }
        });

        this.initDownload();
        this.nextTick(() => {
            this.backgroundImageLoaded();
        });
        /**
         * 点击空白处事件处理函数
         *
         * @param e 事件对象
        */
        const clickHandler = (e: Event) => {
            const targetElement = e.target as Element;
            const button = targetElement.closest('.download-warp-btn');
            const guideMarkerPic = targetElement.closest('.guide-warp');
            if (!button && !guideMarkerPic) {
                this.data.set('showGuide', false);
            }
        };
        this.data.set('_clickHandler', clickHandler);
        document.addEventListener('click', clickHandler);
    }

    detached(): void {
        this.destroyDownload();
        this.destroyListener();
    }

    // 图片加载完成
    backgroundImageLoaded() {
        const bg = new Image() as HTMLImageElement;
        bg.src = this.data.get('wiseBgDark');
        
        bg.onload = () => {
            // 图片加载完成，延迟提升体验
            setTimeout(() => {
                this.data.set('isBackgroundLoaded', true);
            }, 150);
        };
        
        bg.onerror = () => {
            setTimeout(() => {
                this.data.set('isBackgroundLoaded', true);
            }, 150);
        };
    }

    // 销毁 下载标签
    destroyDownload() {
        let downloadDom = this.data.get('downloadDom');
        if (downloadDom) {
            document.body.removeChild(downloadDom);
            downloadDom = null;
            this.data.set('downloadDom', null);
        }
    }
    // 销毁 监听事件
    destroyListener() {
        window.removeEventListener('resize', this.sectionHeight);
        const clickHandler = this.data.get('_clickHandler');
        if (clickHandler) {
            document.removeEventListener('click', clickHandler);
        }
    }

    // 移动端状态栏高度适配
    sectionHeight() {
        const container = this.ref('wiseContainer');
        const height = window.innerHeight + 'px';
        // 适配容器高度
        if (container instanceof HTMLElement) {
            container.style.height = height;
        }
        // 首次进入时记录高度
        if (!this.data.get('originHeight')) {
            this.data.set('originHeight', height);
        }
        // 适配各屏高度
        const arr = [1, 2, 3, 4, 5];
        arr.forEach(item => {
            const section = this.ref(`wiseSection${item}`);
            if (section instanceof HTMLElement) {
                section.style.height = height;
            }
        });
    }

    /**
     * 背景动画
     */
    backgroundAnimate() {
        const rootFontSize = this.data.get('rootFontSize');
        // 第 1 屏到第 2 屏的背景动画
        ScrollTrigger.create({
            trigger: '.wise-section-2',
            start: 'top center',
            end: `top ${rootFontSize}px`,
            scrub: 5,
        });
    }

    /**
     * 分页动画
     */
    paginationAnimate() {
        for (let i = 1; i <= 5; i++) {
            const sectionObserver = new IntersectionObserver(entries => {
                if (entries[0].isIntersecting) {
                    if (i === 1) {
                        this.data.set('paginationColor', '#FFFFFF');
                        this.data.set('paginationList', [1, 0, 0, 0, 0]);
                    }
                    else {
                        this.data.set('paginationColor', '#1E1F24');
                        const paginationList = [0, 0, 0, 0, 0];
                        paginationList[i - 1] = 1;
                        this.data.set('paginationList', paginationList);
                    }
                }
            }, {
                root: null,
                rootMargin: '0px',
                threshold: 0.7,
            });

            const section = document.querySelector(`.wise-section-${i}`);
            section && sectionObserver?.observe(section);
        }
    }

    // 图片动画
    imageAnimate() {
        const rootFontSize = this.data.get('rootFontSize');
        for (let i = 2; i <= 5; i++) {
            ScrollTrigger.create({
                trigger: '.wise-section-' + i,
                start: `top ${rootFontSize}px`,
                end: `top ${rootFontSize}px`,
                scrub: 2,
                animation: gsap.from(`.wise-section-img-${i - 1}`, {y: 50, opacity: 0}),
            });
        }
    }

    // 按钮动画
    buttonAnimate() {
        const rootFontSize = this.data.get('rootFontSize');
        ScrollTrigger.create({
            trigger: '.wise-section-2',
            start: `top-=${rootFontSize * 2}px ${rootFontSize}px`,
            end: `top ${rootFontSize}px`,
            scrub: 1,
            animation: gsap.to('.download-warp-btn', {y: 41, ease: 'slow(0.7,0.7,false)', duration: 0.32}),
        });
    }

    // 自动识别下载链接
    getAutoUrl() {
        return isAndroid() ? this.data.get('downloadAndroidUrl') : isIOS() ? this.data.get('downloadIosUrl') : '';
    }

    // 处理下载按钮点击
    handleDownloadClick() {
        this.handleDownload();
    }

    // 处理下载逻辑
    handleDownload() {
        const downloadUrl =  this.getAutoUrl();
        if (downloadUrl) {
            this.downloadApp(downloadUrl);
            return;
        }
        const errorMsg =  isAndroid() ? '安卓' : isIOS() ? 'iOS' : '鸿蒙';
        this.showToast(`${errorMsg}端APP敬请期待~`);
    }

    // toast 提示
    showToast(message: string) {
        Toast.show({
            message,
        });
    }

    // 下载app
    downloadApp(appUrl: string) {
        const downloadDom = this.data.get('downloadDom');
        if (downloadDom) {
            downloadDom.href = appUrl;
            downloadDom.download = 'xinxiang_app';
            downloadDom.click();
            if (isWeixinApp() && isIOS()) {
                this.data.set('showGuide', true);
            }
        }
    }

    // 初始化下载a标签
    initDownload() {
        const a = document.createElement('a');
        a.style.display = 'none';
        document.body.appendChild(a);
        this.data.set('downloadDom', a);
    }

    // 播放/暂停/结束
    handleVidoe(action: string) {
        handleLog({
            category: 'video',
            action,
            label: 'homepage',
            value: 1,
        });
    }
}